// ═══════════════════════════════════════════════════════════════════════════
// drafts.js — auto-save in-progress episode briefs (mini-spec #67)
//
// API:
//   saveDraft(userId, draft)        → upserts; returns the row
//   listDrafts(userId, opts)        → recent in-progress drafts
//   getDraft(userId, draftId)       → one row
//   markConsumed(userId, draftId)   → status = 'consumed' (called after
//                                     successful generation)
//   deleteDraft(userId, draftId)    → hard delete
//
// The autoSaveDraft helper debounces calls (1500ms) so we don't write
// on every keystroke. localStorage shim writes locally too so a refresh
// before the first round-trip doesn't lose state.
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from './supabase';

const LOCAL_KEY = 'episode_drafts_local_v1';

function readLocal() {
  try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}'); }
  catch { return {}; }
}
function writeLocal(map) {
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(map || {})); } catch {}
}

/**
 * Upsert a draft. Pass an existing draft.id to update; omit it to insert
 * a new row. Returns the saved row (with id populated).
 */
export async function saveDraft(userId, draft) {
  if (!userId) return null;
  const payload = {
    user_id:    userId,
    show_id:    draft.show_id || null,
    topic:      (draft.topic || '').slice(0, 200),
    brief:      draft.brief || {},
    chat_state: draft.chat_state || {},
    status:     draft.status || 'in_progress',
  };
  if (draft.id) payload.id = draft.id;

  // Local backup so a hard refresh between insert + DB ack doesn't drop
  // state. Keyed by topic-truncated id.
  const local = readLocal();
  const localKey = draft.id || `tmp_${Date.now()}`;
  local[localKey] = { ...payload, _localUpdatedAt: Date.now() };
  writeLocal(local);

  if (!supabase) return { ...payload, id: localKey, _local: true };

  try {
    const { data, error } = await supabase
      .from('episode_drafts')
      .upsert(payload, { onConflict: 'id' })
      .select()
      .single();
    if (error) throw error;
    // Replace the tmp key with the real id in localStorage
    if (localKey !== data.id) {
      delete local[localKey];
      local[data.id] = { ...data, _localUpdatedAt: Date.now() };
      writeLocal(local);
    }
    return data;
  } catch (err) {
    console.warn('saveDraft (DB) failed, kept local copy:', err?.message);
    return { ...payload, id: localKey, _local: true };
  }
}

/**
 * List recent drafts for the user. Filters to in_progress by default.
 * Falls back to localStorage when supabase is unavailable.
 */
export async function listDrafts(userId, opts = {}) {
  const status = opts.status || 'in_progress';
  const limit  = Math.max(1, Math.min(50, opts.limit || 10));

  if (supabase && userId) {
    try {
      const { data, error } = await supabase
        .from('episode_drafts')
        .select('id, topic, brief, status, show_id, created_at, updated_at')
        .eq('user_id', userId)
        .eq('status',  status)
        .order('updated_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.warn('listDrafts (DB) failed:', err?.message);
    }
  }

  // Local fallback — flat sort by _localUpdatedAt desc.
  const local = readLocal();
  return Object.values(local)
    .filter(d => d.status === status)
    .sort((a, b) => (b._localUpdatedAt || 0) - (a._localUpdatedAt || 0))
    .slice(0, limit);
}

export async function getDraft(userId, draftId) {
  if (!draftId) return null;
  if (supabase && userId && !String(draftId).startsWith('tmp_')) {
    try {
      const { data } = await supabase
        .from('episode_drafts')
        .select('*')
        .eq('id', draftId)
        .eq('user_id', userId)
        .maybeSingle();
      if (data) return data;
    } catch {}
  }
  const local = readLocal();
  return local[draftId] || null;
}

export async function markConsumed(userId, draftId) {
  if (!draftId) return;
  if (supabase && userId && !String(draftId).startsWith('tmp_')) {
    try {
      await supabase
        .from('episode_drafts')
        .update({ status: 'consumed' })
        .eq('id', draftId)
        .eq('user_id', userId);
    } catch {}
  }
  const local = readLocal();
  if (local[draftId]) {
    local[draftId].status = 'consumed';
    writeLocal(local);
  }
}

export async function deleteDraft(userId, draftId) {
  if (!draftId) return;
  if (supabase && userId && !String(draftId).startsWith('tmp_')) {
    try {
      await supabase
        .from('episode_drafts')
        .delete()
        .eq('id', draftId)
        .eq('user_id', userId);
    } catch {}
  }
  const local = readLocal();
  delete local[draftId];
  writeLocal(local);
}

/**
 * Debounced auto-save. Call from any input onChange — only the last
 * call within DEBOUNCE_MS actually fires saveDraft(). Returns the
 * promise of the last save (or null) so the caller can await if it
 * needs the resulting id.
 *
 * Usage:
 *   useEffect(() => autoSaveDraft(user.id, { brief, topic }), [brief]);
 */
const DEBOUNCE_MS = 1500;
let pendingTimer = null;
let pendingResolve = null;

export function autoSaveDraft(userId, draft) {
  if (pendingTimer) clearTimeout(pendingTimer);
  return new Promise((resolve) => {
    pendingResolve = resolve;
    pendingTimer = setTimeout(async () => {
      try {
        const row = await saveDraft(userId, draft);
        resolve(row);
      } catch {
        resolve(null);
      }
    }, DEBOUNCE_MS);
  });
}
